import { createPortal } from 'react-dom'
import { useEffect, useLayoutEffect, useRef, useState, type ReactElement } from 'react'
import type { Demand } from '@/types/knowledge_graph'
import { InfoIcon } from 'lucide-react'

export function DemandRefBadge ({ demandId, demand }: { demandId: string, demand: Demand | undefined }): ReactElement {
  const [hovered, setHovered] = useState(false)
  const [pinned, setPinned] = useState(false)
  const anchorRef = useRef<HTMLSpanElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const [placement, setPlacement] = useState<'below' | 'above'>('below')
  const open = hovered || pinned

  useEffect(() => {
    if (open) setRect(anchorRef.current?.getBoundingClientRect() ?? null)
  }, [open])

  useLayoutEffect(() => {
    if (!open || (rect == null)) return
    const popoverHeight = popoverRef.current?.offsetHeight ?? 0
    const spaceBelow = window.innerHeight - rect.bottom
    setPlacement(spaceBelow < popoverHeight + 12 ? 'above' : 'below')
  }, [open, rect])

  useEffect(() => {
    if (!pinned) return
    function handleClickOutside (e: MouseEvent): void {
      if (anchorRef.current?.contains(e.target as Node) !== true) setPinned(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [pinned])

  return (
    <span
      ref={anchorRef}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 3, cursor: 'pointer' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => setPinned((p) => !p)}
    >
      {demandId}
      <InfoIcon width={12} height={12} style={{ color: 'var(--bp-accent)', flexShrink: 0 }} />
      {open && (rect != null) && createPortal(
        <div
          ref={popoverRef}
          style={{
            position: 'fixed',
            ...(placement === 'below'
              ? { top: rect.bottom + 6 }
              : { top: 'auto', bottom: window.innerHeight - rect.top + 6 }
            ),
            left: Math.min(rect.left, window.innerWidth - 300),
            width: 280,
            zIndex: 1000,
            background: 'var(--bp-surface)',
            border: '1.5px solid var(--bp-border)',
            borderRadius: 8,
            padding: '12px 14px',
            boxShadow: '0 12px 32px rgba(0,0,0,.18)'
          }}
        >
          {(demand != null)
            ? (
              <>
                <div className='kg-demand-id'>{demand.id}</div>
                <div className='kg-demand-title'>{demand.name}</div>
                <div className='kg-demand-body'>{demand.description}</div>
              </>
              )
            : (
              <div style={{ fontSize: 12, color: 'var(--bp-ink3)' }}>要求が見つかりません（{demandId}）</div>
              )}
        </div>,
        document.body
      )}
    </span>
  )
}
